"use client";

import {
  BookOpen,
  FolderTree,
  Settings,
  SlidersHorizontal,
} from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { VybeAgentMark } from "@/components/desktop/agent/VybeAgentMark";
import { cn } from "@/lib/utils";
import {
  NAVIGATOR_SECTIONS,
  type NavigatorSection,
} from "./navigatorTypes";

const SECTION_META: Record<
  NavigatorSection,
  { title: string; ariaLabel: string }
> = {
  files: { title: "Files", ariaLabel: "Show files" },
  agent: { title: "Vybe Agent", ariaLabel: "Show Vybe Agent" },
  settings: { title: "Settings", ariaLabel: "Show settings" },
  inputs: { title: "Input preview", ariaLabel: "Show input preview" },
  docs: { title: "Documentation", ariaLabel: "Show documentation" },
};

function SectionIcon({
  section,
  active,
}: {
  section: NavigatorSection;
  active: boolean;
}) {
  const className = "h-3.5 w-3.5";
  switch (section) {
    case "files":
      return <FolderTree className={className} />;
    case "agent":
      return (
        <VybeAgentMark
          size={14}
          tone={active ? "primary" : "foreground"}
        />
      );
    case "settings":
      return <Settings className={className} />;
    case "inputs":
      return <SlidersHorizontal className={className} />;
    case "docs":
      return <BookOpen className={className} />;
    default:
      return null;
  }
}

export default function NavigatorSectionToolbar({
  section,
  onSectionChange,
}: {
  section: NavigatorSection;
  onSectionChange: (section: NavigatorSection) => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      {NAVIGATOR_SECTIONS.map((id) => {
        const active = section === id;
        const meta = SECTION_META[id];
        return (
          <Button
            key={id}
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "h-7 w-7 shrink-0",
              active && "bg-primary/10 text-primary",
            )}
            onClick={() => onSectionChange(id)}
            title={meta.title}
            aria-label={meta.ariaLabel}
            aria-pressed={active}
          >
            <SectionIcon section={id} active={active} />
          </Button>
        );
      })}
    </div>
  );
}
